import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function lim(v) {
  return v != null ? String(v) : 'Unlimited';
}

export default function PlatformUsagePolicyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/usage-policies/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setPolicy(json.policy);
    } catch (e) {
      setError(e.message);
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !policy) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!policy) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{policy.policy_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {policy.policy_scope}
            {policy.plan_id != null ? ` · Plan #${policy.plan_id}` : ''}
            {policy.tenant_id != null ? ` · Tenant #${policy.tenant_id}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/platform/usage-policies')}>
            Back
          </Button>
          {isSuper && (
            <Button variant="contained" onClick={() => navigate(`/platform/usage-policies/${id}/edit`)}>
              Edit
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('ID', policy.id)}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip size="small" label={policy.status} />
            </Box>
          </Grid>
          {row('Enforcement mode', policy.enforcement_mode)}
          {row('Platform default', policy.is_platform_default ? 'Yes' : 'No')}
          {row('Plan', policy.plan_name || (policy.plan_id != null ? `#${policy.plan_id}` : '—'))}
          {row(
            'Tenant',
            policy.tenant_id != null
              ? `${policy.tenant_name || '—'} (${policy.tenant_code || policy.tenant_id})`
              : '—'
          )}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Quotas
        </Typography>
        <Grid container spacing={2}>
          {row('Requests / minute', lim(policy.request_limit_per_minute))}
          {row('Daily requests', lim(policy.daily_request_limit))}
          {row('Monthly requests', lim(policy.monthly_request_limit))}
          {row('Concurrent requests', lim(policy.concurrent_request_limit))}
          {row('Burst', lim(policy.burst_limit))}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {row('Created', policy.created_at ? new Date(policy.created_at).toLocaleString() : '—')}
          {row('Updated', policy.updated_at ? new Date(policy.updated_at).toLocaleString() : '—')}
        </Grid>
      </Paper>
    </Box>
  );
}
